import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { BulkMessagingQueries } from '@frontend/api-bulk-messaging';
import { useFeatureAccess } from '@frontend/feature-access';
import { useTranslation } from '@frontend/i18n';
import { useBulkDraftShallowStore } from '@frontend/messages';
import { useAppScopeStore } from '@frontend/scope';
import { createNavItem, createSubNavItems } from '../nav-item-utils';

export const useMarketingNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const { hasAccess: hasBulkTextAccess } = useFeatureAccess('commx-bulk-messaging', Permission.BULK_TEXTING_MANAGE);
  const { hasAccess: hasBulkEmailAccess } = useFeatureAccess(
    'commx-email-marketing-v2',
    Permission.EMAIL_MARKETING_MANAGE
  );
  const { initialYear: initialBulkDraftYear } = useBulkDraftShallowStore('initialYear');
  const bulkDraftYear = initialBulkDraftYear ? Number(initialBulkDraftYear) : new Date().getFullYear();
  const { count: bulkMessageDraftCount } = BulkMessagingQueries.useListCampaignDrafts(
    {
      locationIds: selectedLocationIds,
      orgId: selectedOrgId,
      year: bulkDraftYear,
    },
    hasBulkEmailAccess,
    hasBulkTextAccess
  );

  const subNavItems = useMemo(
    () =>
      createSubNavItems([
        {
          label: t('All Campaigns'),
          relativePath: '/all-campaigns/email',
          trackingId: 'nav-marketing-all-campaings',
          description:
            'Lists all email and text marketing campaigns, both active and historical, with performance details for each.',
        },
        {
          label: t('Drafts'),
          relativePath: '/drafts',
          count: bulkMessageDraftCount,
          isPassiveCount: true,
          trackingId: 'nav-marketing-drafts',
          description: 'Shows text marketing campaigns still in draft form, allowing further edits before launch.',
        },
      ]),
    [t, bulkMessageDraftCount]
  );

  return useMemo(
    () =>
      createNavItem('marketing', {
        iconName: 'bulk-messaging',
        label: t('Marketing'),
        root: '/bulk',
        show: true,
        subNavItems,
      }),
    [t, subNavItems]
  );
};
